<template>
  <div class="mt-4">
    <v-alert
      v-if="successMessage"
      class="mb-4"
      closable
      density="comfortable"
      type="success"
      variant="tonal"
      @click:close="successMessage = null"
    >
      {{ successMessage }}
    </v-alert>

    <v-alert
      v-if="errorMessage"
      class="mb-4"
      closable
      density="comfortable"
      type="error"
      variant="tonal"
      @click:close="errorMessage = null"
    >
      {{ errorMessage }}
    </v-alert>

    <v-row>
      <v-col cols="12" md="5">
        <v-card>
          <v-card-item>
            <v-card-title>Authenticator App</v-card-title>
            <v-card-subtitle>Protect sign-in with a 6-digit code from Authy or Microsoft Authenticator.</v-card-subtitle>
          </v-card-item>
          <v-card-text>
            <div class="d-flex align-center mb-4">
              <strong class="mr-3">Status</strong>
              <v-chip :color="statusColor" label>{{ statusLabel }}</v-chip>
            </div>

            <div class="text-body-2 mb-4">
              Issuer: <strong>{{ status.issuer || 'Mose' }}</strong>
            </div>

            <v-text-field
              v-if="status.enabled"
              :model-value="setupCurrentCode"
              @update:modelValue="setupCurrentCode = normalizeCode($event)"
              class="mb-2"
              :counter="6"
              hint="Required when replacing the current authenticator app."
              inputmode="numeric"
              label="Current Authenticator Code"
              maxlength="6"
              persistent-hint
            ></v-text-field>

            <v-btn
              block
              color="primary"
              :loading="setupLoading"
              @click="startSetup"
            >
              {{ status.enabled ? 'Replace Authenticator' : 'Start Setup' }}
            </v-btn>

            <div class="text-caption mt-3" v-if="status.pendingSetup">
              A pending setup already exists. Verify a fresh code to finish enabling it, or start setup again to replace the pending secret.
            </div>
          </v-card-text>
        </v-card>

        <v-card class="mt-4" v-if="status.enabled">
          <v-card-item>
            <v-card-title>Disable Authenticator</v-card-title>
            <v-card-subtitle>Enter a current 6-digit code before turning MFA off.</v-card-subtitle>
          </v-card-item>
          <v-card-text>
            <v-text-field
              :model-value="disableCode"
              @update:modelValue="disableCode = normalizeCode($event)"
              :counter="6"
              inputmode="numeric"
              label="Current Authenticator Code"
              maxlength="6"
            ></v-text-field>
            <v-btn
              block
              color="error"
              :loading="disableLoading"
              @click="disableAuthenticator"
            >
              Disable Authenticator
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="7">
        <v-card>
          <v-card-item>
            <v-card-title>{{ setupCardTitle }}</v-card-title>
            <v-card-subtitle>{{ setupCardSubtitle }}</v-card-subtitle>
          </v-card-item>
          <v-card-text>
            <template v-if="setupData">
              <v-row class="align-center">
                <v-col cols="12" sm="5">
                  <div class="qr-code-wrap">
                    <img
                      v-if="qrCodeDataUrl"
                      :src="qrCodeDataUrl"
                      alt="Authenticator app QR code"
                      class="qr-code-image"
                    />
                    <v-progress-circular
                      v-else
                      color="primary"
                      indeterminate
                    ></v-progress-circular>
                  </div>
                </v-col>

                <v-col cols="12" sm="7">
                  <div class="text-body-2 mb-3">
                    Scan the QR code, or enter the manual key below if your app is on another device.
                  </div>

                  <v-text-field
                    :model-value="setupData.accountName"
                    label="Account"
                    readonly
                  ></v-text-field>

                  <v-text-field
                    :model-value="setupData.secret"
                    append-inner-icon="mdi-content-copy"
                    label="Manual Key"
                    readonly
                    @click:append-inner="copyText(setupData.secret, 'Manual key')"
                  ></v-text-field>

                  <v-textarea
                    :model-value="setupData.otpauthUrl"
                    append-inner-icon="mdi-content-copy"
                    auto-grow
                    label="otpauth URL"
                    readonly
                    rows="2"
                    @click:append-inner="copyText(setupData.otpauthUrl, 'otpauth URL')"
                  ></v-textarea>
                </v-col>
              </v-row>
            </template>

            <v-alert
              v-else-if="status.pendingSetup"
              class="mb-4"
              density="comfortable"
              type="warning"
              variant="tonal"
            >
              A setup is pending, but the secret is not available anymore on this page. If you already added the account in your authenticator app, enter a fresh code below to finish enabling it. Otherwise start setup again to generate a new key.
            </v-alert>

            <template v-if="status.pendingSetup || setupData">
              <v-text-field
                :model-value="enableCode"
                @update:modelValue="enableCode = normalizeCode($event)"
                :counter="6"
                inputmode="numeric"
                label="Verification Code"
                maxlength="6"
              ></v-text-field>

              <v-btn
                color="success"
                :loading="enableLoading"
                @click="enableAuthenticator"
              >
                Enable Authenticator
              </v-btn>
            </template>

            <template v-else>
              <div class="text-body-2 mb-4">
                Start setup to generate a new TOTP secret for your account. The next screen will show a QR code and manual key, then you will confirm setup with one 6-digit code from the app.
              </div>

              <v-list density="compact">
                <v-list-item prepend-icon="mdi-numeric-1-circle-outline" title="Start setup from this page"></v-list-item>
                <v-list-item prepend-icon="mdi-numeric-2-circle-outline" title="Scan the QR code or enter the manual key in your authenticator app"></v-list-item>
                <v-list-item prepend-icon="mdi-numeric-3-circle-outline" title="Enter one fresh 6-digit code here to enable MFA"></v-list-item>
              </v-list>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from 'axios';
import QRCode from 'qrcode';
import { getApiErrorMessage, isPlainUnauthorized } from '../utils/api';
import { clearAuthSession } from '../utils/authSession';

const defaultStatus = {
  enabled: false,
  pendingSetup: false,
  issuer: 'Mose',
};

export default {
  name: 'AuthenticatorPage',
  data() {
    return {
      status: { ...defaultStatus },
      setupData: null,
      qrCodeDataUrl: null,
      setupCurrentCode: '',
      enableCode: '',
      disableCode: '',
      setupLoading: false,
      enableLoading: false,
      disableLoading: false,
      errorMessage: null,
      successMessage: null,
    };
  },
  computed: {
    statusLabel() {
      if (this.status.enabled) {
        return 'Enabled';
      }

      if (this.status.pendingSetup) {
        return 'Pending Setup';
      }

      return 'Disabled';
    },
    statusColor() {
      if (this.status.enabled) {
        return 'success';
      }

      if (this.status.pendingSetup) {
        return 'warning';
      }

      return 'default';
    },
    setupCardTitle() {
      if (this.setupData) {
        return 'Finish Setup';
      }

      if (this.status.pendingSetup) {
        return 'Pending Setup';
      }

      return 'Setup Instructions';
    },
    setupCardSubtitle() {
      if (this.setupData) {
        return 'Scan the QR code or enter the manual key, then confirm with a fresh 6-digit code.';
      }

      if (this.status.pendingSetup) {
        return 'A secret is already pending for this account.';
      }

      return 'This page manages the MFA flow for your account.';
    },
  },
  async created() {
    await this.fetchStatus();
  },
  methods: {
    normalizeCode(value) {
      return String(value || '').replace(/\D/g, '').slice(0, 6);
    },
    redirectToLogin() {
      clearAuthSession();
      this.$store.commit('setLoggedIn', false);
      this.$router.push('/login');
    },
    clearFeedback() {
      this.errorMessage = null;
      this.successMessage = null;
    },
    async fetchStatus() {
      try {
        const response = await axios.get(process.env.VUE_APP_API_BASE_URL + '/user/authenticator');
        this.status = {
          ...defaultStatus,
          ...response.data,
        };

        if (!this.status.pendingSetup) {
          this.setupData = null;
          this.qrCodeDataUrl = null;
        }
      } catch (error) {
        if (isPlainUnauthorized(error)) {
          this.redirectToLogin();
          return;
        }

        this.errorMessage = getApiErrorMessage(error, 'Failed to load authenticator status.');
      }
    },
    async startSetup() {
      this.clearFeedback();

      if (this.status.enabled && this.setupCurrentCode.length !== 6) {
        this.errorMessage = 'Enter your current 6-digit authenticator code to replace the existing setup.';
        return;
      }

      this.setupLoading = true;

      try {
        const payload = {};

        if (this.status.enabled) {
          payload.currentCode = this.setupCurrentCode;
        }

        const response = await axios.post(
          process.env.VUE_APP_API_BASE_URL + '/user/authenticator/setup',
          payload,
          { skipAuthRedirect: true }
        );

        this.setupData = response.data;
        this.enableCode = '';
        this.setupCurrentCode = '';
        this.qrCodeDataUrl = await QRCode.toDataURL(response.data.otpauthUrl, {
          errorCorrectionLevel: 'M',
          margin: 1,
          width: 240,
        });
        this.status = {
          ...this.status,
          pendingSetup: true,
        };
        this.successMessage = response.data.message;
      } catch (error) {
        if (isPlainUnauthorized(error)) {
          this.redirectToLogin();
          return;
        }

        this.errorMessage = getApiErrorMessage(error, 'Failed to start authenticator setup.');
      } finally {
        this.setupLoading = false;
      }
    },
    async enableAuthenticator() {
      this.clearFeedback();

      if (this.enableCode.length !== 6) {
        this.errorMessage = 'Enter a valid 6-digit verification code.';
        return;
      }

      this.enableLoading = true;

      try {
        const response = await axios.post(
          process.env.VUE_APP_API_BASE_URL + '/user/authenticator/enable',
          { code: this.enableCode },
          { skipAuthRedirect: true }
        );

        this.enableCode = '';
        this.setupCurrentCode = '';
        this.setupData = null;
        this.qrCodeDataUrl = null;
        this.successMessage = response.data.message;
        await this.fetchStatus();
      } catch (error) {
        if (isPlainUnauthorized(error)) {
          this.redirectToLogin();
          return;
        }

        this.errorMessage = getApiErrorMessage(error, 'Failed to enable the authenticator.');
      } finally {
        this.enableLoading = false;
      }
    },
    async disableAuthenticator() {
      this.clearFeedback();

      if (this.disableCode.length !== 6) {
        this.errorMessage = 'Enter your current 6-digit authenticator code.';
        return;
      }

      this.disableLoading = true;

      try {
        const response = await axios.post(
          process.env.VUE_APP_API_BASE_URL + '/user/authenticator/disable',
          { code: this.disableCode },
          { skipAuthRedirect: true }
        );

        this.disableCode = '';
        this.setupCurrentCode = '';
        this.enableCode = '';
        this.setupData = null;
        this.qrCodeDataUrl = null;
        this.successMessage = response.data.message;
        await this.fetchStatus();
      } catch (error) {
        if (isPlainUnauthorized(error)) {
          this.redirectToLogin();
          return;
        }

        this.errorMessage = getApiErrorMessage(error, 'Failed to disable the authenticator.');
      } finally {
        this.disableLoading = false;
      }
    },
    async copyText(value, label) {
      try {
        await navigator.clipboard.writeText(value);
        this.successMessage = `${label} copied.`;
      } catch {
        this.errorMessage = `Failed to copy ${label.toLowerCase()}.`;
      }
    },
  },
};
</script>

<style scoped>
.qr-code-wrap {
  align-items: center;
  background: #f5f5f5;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  min-height: 260px;
  padding: 16px;
}

.qr-code-image {
  background: #ffffff;
  border-radius: 8px;
  display: block;
  max-width: 100%;
  padding: 12px;
}
</style>
